import cboImg from "@/assets/company/CBO.jpg";
import ceoImg from "@/assets/company/CEO.jpg";
import cmoImg from "@/assets/company/CMO.png";
import cpoImg1 from "@/assets/company/CPO.jpg";
import cpoImg from "@/assets/company/CPO.png";
import ctoImg from "@/assets/company/CTO.png";
import ethics from "@/assets/company/about-ethics.png";
import mission from "@/assets/company/about-mission.png";
import cooImg from "@/assets/company/coo.png";
import heroVideo from "@/assets/company/video.mp4";
import logo01 from "@/assets/home/logo/01_PROYA.png";
import logo02 from "@/assets/home/logo/02_P&G_.png";
import logo03 from "@/assets/home/logo/03_PLUG_AND_PLAY_CHINA.png";
import logo04 from "@/assets/home/logo/04_Transsion.png";
import logo05 from "@/assets/home/logo/05_KERING_FOUNDATION.png";
import logo06 from "@/assets/home/logo/06_dingchedi.png";
import logo07 from "@/assets/home/logo/07_CARSLAN.png";
import logo08 from "@/assets/home/logo/08_lebo.png";
import logo09 from "@/assets/home/logo/09_Timekettle.png";
import { useT } from "@/lib/i18n";
import { Maximize, Pause, Play, Volume2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";

interface CompanyPageProps {
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
}

const CompanyPage: React.FC<CompanyPageProps> = () => {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isPlaying]);

  const toggleFullscreen = useCallback(() => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      setProgress(current);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  }, []);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setProgress(time);
    }
  }, []);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);

  const founders = [
    {
      name: t("Leo Liu", "刘昆"),
      role: t("CEO", "CEO"),
      desc: t("Bachelor's and Master's degrees in Architecture from Tongji University. Founder of AID Lab; Founder & CEO of Oran AI; named to the 2025 Chinese American Elite 30 Under 30.", "同济建筑系本硕，AID Lab 创始人，橙果视界创始人 & CEO，2025 全美华人精英 30U30。"),
      avatar: ceoImg,
    },
    {
      name: t("Vera Hou", "侯昭薇"),
      role: t("COO", "COO"),
      desc: t("An experienced traffic growth operator with tens of millions of views generated, and a self-media influence reaching the million-level audience scale. Previously founded and led a 20+ member team that achieved annual revenue of RMB 30 million+.", "千万流量操盘手，具备百万级自媒体影响力，曾创立并带领 20+ 人团队实现年营收3000万+。"),
      avatar: cooImg,

    },
    {
      name: t("Yutong Cai", "蔡雨彤"),
      role: t("CBO", "CBO"),
      desc: t("Former Brand Director at Dewu, Alibaba, and Kering. Contributor to fashion magazines including Harper's Bazaar and ELLE, and a Xiaohongshu blogger focused on AI and workplace topics.", "前得物/阿里/开云品牌总监，《时尚芭莎》《ELLE》等时尚杂志撰稿人，小红书AI+职场博主。"),
      avatar: cboImg,
    },
    {
      name: t("Mingfu Zhang", "张铭夫"),
      role: t("CTO", "CTO"),
      desc: t("PhD in Computer Graphics from Politecnico di Milano. Member of Laura Loredana CG Studio and core developer in the Blender community.", "米兰理工计算机图形博士，Laura Loredana CG 工作室成员，Blender 社区核心开发者。"),
      avatar: ctoImg,

    },
    {
      name: t("Mingyi Li", "李明乙"),
      role: t("CPO", "CPO"),
      desc: t("Bachelor's and Master's degrees in Architecture from Tongji University. Built the world's first end-to-end content marketing delivery platform based on a multi-agent architecture, and led the 0-to-1 implementation of core AI products including PhotoG and OranSim.", "同济建筑系本硕，打造全球首个基于多智能体架构的内容营销端到端交付平台，从 0 到 1 统筹 PhotoG、OranSim 等核心 AI 产品落地。"),
      avatar: cpoImg,
    },
    {
      name: t("Nikki Li", "李纯妮"),
      role: t("CMO", "CMO"),
      desc: t("Operator of global expansion businesses at the tens-of-billions scale, with over 15 years of experience in internet advertising and marketing. Former Head of the E-commerce Brand Business Unit at Feishu Shennuo Group.", "百亿级出海业务操盘手，拥有 15 年以上互联网广告营销经验，曾任飞书深诺集团电商品牌事业部负责人。"),
      avatar: cmoImg,
    },
    {
      name: t("Yu Zhao", "赵宇"),
      role: t("CPO", "CPO"),
      desc: t("Former Product Expert at Alibaba. Previously led a product business line at Trip.com, generating over RMB 8 million in annual revenue from a single project. Also led the 0-to-1 launch of Alibaba's marketing interaction product, which surpassed one million users within one month.", "前阿里产品专家，曾主导携程产品业务线，单项目年收益 800 万+；主导阿里营销互动产品从 0 到 1 上线，1 个月用户突破百万。"),
      avatar: cpoImg1,

    },
  ];

  const investors = [
    { name: "Accel", logo: logo01 },
    { name: "GV", logo: logo02 },
    { name: "NEA", logo: logo03 },
    { name: "PSP", logo: logo04 },
    { name: "ASC", logo: logo05 },
    { name: "MMC", logo: logo06 },
    { name: "Wi", logo: logo07 },
    { name: "AV", logo: logo08 },
    { name: "FM", logo: logo09 },
  ];

  return (
    <div className="landing-v2 text-foreground bg-background">
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden flex items-center pt-28">
        <div className="relative z-10 text-center max-w-[min(1200px,92vw)] mx-auto">
          <h1 className="text-5xl md:text-7xl font-semibold tracking-tight leading-[1.05] text-foreground mb-8">
            {t("The All-in-one AI Marketing Platform for Business", "一站式 AI 营销平台")}
          </h1>
          <div className="mt-12 relative rounded-2xl overflow-hidden aspect-video max-w-[1200px] mx-auto nebula-border bg-white/5 group">
            <video
              ref={videoRef}
              src={heroVideo}
              playsInline
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={handlePlay}
              onPause={handlePause}
              className="w-full h-full object-cover"
            />

            {/* Initial Play Button Overlay */}
            {!isPlaying && (
              <button
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
              >
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--brand))]/90 flex items-center justify-center hover:scale-110 transition-transform shadow-lg">
                  <Play size={32} className="text-white fill-white ml-1" />
                </div>
              </button>
            )}

            {/* Controls Bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="p-4 flex items-center gap-3">
                {/* Play/Pause Button */}
                <button
                  onClick={togglePlay}
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} className="fill-white" />}
                </button>

                {/* Progress Bar */}
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-white text-xs min-w-[40px] text-right">
                    {formatTime(progress)}
                  </span>
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={progress}
                    onChange={handleSeek}
                    className="flex-1 h-1 bg-white/30 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:hover:scale-125 [&::-webkit-slider-thumb]:transition-transform"
                  />
                  <span className="text-white text-xs min-w-[40px]">
                    {formatTime(duration)}
                  </span>
                </div>

                {/* Volume Icon */}
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Volume2 size={20} />
                </div>

                {/* Fullscreen Button */}
                <button
                  onClick={toggleFullscreen}
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  <Maximize size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR MISSION */}
      <section id="about" className="relative py-32 px-6">
        <div className="mx-auto max-w-[min(1200px,92vw)]">
          <div className="grid md:grid-cols-[8fr_12fr] gap-16 md:gap-20 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-20">
                {t("About OranAI", "关于橙果视界")}
              </h2>
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t("Mission", "使命")}</h3>
                  <p className="text-muted-foreground">
                    {t("Empowering the world with AI, making brand growth more predictable.", "AI赋能全球，让品牌增长更具确定性")}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t("Vision", "愿景")}</h3>
                  <p className="text-muted-foreground">
                    {t("To become the world's largest AI MCN, enabling every brand's voice to be heard.", "成为全球最大的AI MCN，让世界听见每一个品牌的声音。")}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t("Company Philosophy", "公司理念")}</h3>
                  <p className="text-muted-foreground">
                    {t("OranAI is built around data-driven insights, intelligent models, and closed-loop business execution, helping brands develop AI-powered marketing capabilities for global growth.", "橙果视界以数据洞察、智能模型与业务闭环为核心，帮助品牌构建面向全球化增长的 AI 营销能力。")}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t("Values", "价值观")}</h3>
                  <p className="text-muted-foreground">
                    {t("Aim high, pursue excellence, stay results-oriented, and put customers first.", "志存高远、追求极致、结果导向、客户优先。")}
                  </p>
                </div>
              </div>
            </div>
            <div className="aspect-[3/2] rounded-2xl relative overflow-hidden nebula-border bg-white/5 flex items-center justify-center">
              <img
                src={mission}
                alt="Mission"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BUSINESS DIRECTION */}
      <section className="relative py-32 px-6 bg-white/[0.02]">
        <div className="mx-auto max-w-[min(1200px,92vw)]">
          <div className="grid md:grid-cols-[12fr_8fr] gap-16 md:gap-20 items-center">
            <div className="order-2 md:order-1 aspect-[3/2] rounded-2xl relative overflow-hidden nebula-border bg-white/5 flex items-center justify-center">
              <img
                src={ethics}
                alt="Business Direction"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-20">
                {t("Business Direction", "业务方向")}
              </h2>
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t("Business Overview", "业务内容")}</h3>
                  <p className="text-muted-foreground">
                    {t("OranAI focuses on AI + marketing, serving brand globalization and growth scenarios with full-chain AI marketing solutions across social media marketing, creative advertising, influencer marketing, brand operations, public opinion management, and search optimization. Around market insights, strategy generation, content production, media reach, and AI search visibility, the company helps brands build a sustainable and iterative intelligent marketing system.", "橙果视界深耕 AI + 营销，面向品牌全球化增长场景，提供覆盖社媒营销、创意广告、达人营销、品牌运营、舆情管理与搜索优化的全链路 AI 营销解决方案。公司围绕市场洞察、策略生成、内容生产、媒介触达与 AI 搜索可见性，帮助品牌构建可持续迭代的智能营销体系。")}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t("Core Business Directions", "核心业务方向")}</h3>
                  <p className="text-muted-foreground">
                    {t("Social Media Marketing, Creative Advertising, Influencer Marketing, Brand Operations, Public Opinion Management, Search Optimization.", "社媒营销、创意广告、达人营销、品牌运营、舆情管理、搜索优化。")}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t("Core Capabilities", "核心能力")}</h3>
                  <p className="text-muted-foreground">
                    {t("Market Insights, Strategy Generation, Content Production, Media Reach, AI Search Visibility, Brand Growth Loop.", "市场洞察、策略生成、内容生产、媒介触达、AI 搜索可见性、品牌增长闭环。")}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t("Service Scenarios", "服务场景")}</h3>
                  <p className="text-muted-foreground">
                    {t("Brand Global Growth, Overseas Social Media Communication, AI Search Optimization, Content Production Efficiency, Brand Asset Accumulation, Marketing Campaign Growth.", "品牌全球化增长、海外社媒传播、AI 搜索优化、内容生产提效、品牌资产沉淀、营销活动增长。")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE TEAM */}
      <section className="relative py-32 px-6">
        <div className="mx-auto max-w-[min(1200px,92vw)]">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-center mb-6">
            {t("Core Team", "核心团队")}
          </h2>
          <p className="text-muted-foreground text-center mb-6 max-w-4xl mx-auto text-lg">
            {t("OranAI is led by a young and dynamic core team. Starting from AID Lab, an AIGC open-source community, the company has gradually grown into a multidisciplinary team with capabilities across AI technology, brand marketing, and commercial implementation. The team continues to explore practical AI applications in marketing scenarios, driving the evolution of brand marketing from labor-intensive execution toward intelligent, systematic growth through product R&D, strategic insights, content generation, and client delivery.", "橙果视界由年轻活力的核心团队领衔，从 AIGC 开源社区AID Lab起步，逐步成长为一支兼具 AI 技术、品牌营销与商业化落地能力的复合型团队。团队持续探索 AI 在营销场景中的真实落地，围绕产品研发、策略洞察、内容生成与客户交付，推动品牌营销从人力密集型执行走向智能化、系统化增长。")}
          </p>
          <p className="text-muted-foreground text-center mb-16 max-w-4xl mx-auto text-lg">
            {t("Core Management Team:", "核心管理团队包括：")}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {founders.map((founder, i) => (
              <div
                key={i}
                className="rounded-2xl p-8 flex flex-col items-center text-center nebula-border bg-white/5"
              >
                <img
                  src={founder.avatar}
                  alt={founder.name}
                  className="w-26 aspect-square   mb-6  "
                />
                <h3 className="text-xl font-semibold mb-2">{founder.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{founder.role}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{founder.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MILESTONES */}
      <section className="relative py-32 px-6 bg-white/[0.02]">
        <div className="mx-auto max-w-[min(1200px,92vw)]">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-center mb-16">
            {t("Milestones", "发展历程")}
          </h2>
          <div className="space-y-0">
            {[
              {
                year: "2022",
                items: [
                  t(
                    "AID Lab, the world's first open-source AI design community, was founded on Discord.",
                    "全球首个 AI 设计开源社区「AID Lab」在 Discord 成立。",
                  ),
                ],
              },
              {
                year: "2023",
                items: [
                  t(
                    "Released three major algorithm frameworks: NeRF++, Controlspace, and A-T.",
                    "相继发布 NeRF++、Controlspace、A-T 三大算法框架。",
                  ),
                  t(
                    "Continued to push the SOTA of AIGC applications.",
                    "持续刷新 AIGC 应用 SOTA。",
                  ),
                ],
              },
              {
                year: "2024",
                items: [
                  t(
                    "OranAI was officially founded.",
                    "橙果视界正式成立。",
                  ),
                  t(
                    "Secured several million RMB in seed funding from Matrix Design.",
                    "获矩阵股份数百万元种子轮融资。",
                  ),
                  t(
                    "Released DataG, a digital asset management system purchased by multiple listed companies.",
                    "数字资产管理系统 DataG 发布，数家上市公司采购。",
                  ),
                  t(
                    "Oran AI x Tencent Technology: established China's first joint AIGC design laboratory.",
                    "橙果视界 x 腾讯科技：国内首个设计 AIGC 联合实验室成立。",
                  ),
                  t(
                    "Released PhotoG, an intelligent marketing engine that gained strong attention in both overseas and domestic tech communities.",
                    "智能营销引擎 PhotoG 发布，引爆海外与国内科技圈。",
                  ),
                ],
              },
              {
                year: "2025",
                items: [
                  t(
                    "Released PhotoG 2.0, PhotoG 3.0, and OranGEO, an AI search optimization platform, continuously upgrading the product lineup.",
                    "相继发布 PhotoG 2.0、PhotoG 3.0、AI 搜索优化平台 OranGEO，不断升级迭代产品。",
                  ),
                  t(
                    "Secured tens of millions of RMB in angel funding from a leading VC.",
                    "获头部 VC 数千万天使轮融资。",
                  ),
                  t(
                    "Invited to BEYOND EXPO, appearing in the global innovation ecosystem.",
                    "受邀参加 BEYOND EXPO 国际创新博览会，亮相全球生态。",
                  ),
                ],
              },
              {
                year: "2026",
                items: [
                  t(
                    "Released the OranHub, OranSim, OranGen, and OranMed product matrix, officially landing a full-chain AI marketing solution.",
                    "OranHub、OranSim、OranGen、OranMed 四大产品矩阵发布，全链路 AI 营销解决方案正式落地。",
                  ),
                  t(
                    "Hosted the \"2026 First AI-Empowered Brand Globalization Conference.\"",
                    "主办\"2026 首届 AI 赋能品牌全球化大会\"。",
                  ),
                  t(
                    "Secured tens of millions of RMB in Pre-A funding from leading capital.",
                    "获头部资本数千万 Pre-A 轮融资。",
                  ),
                  t(
                    "Plans to onboard 100,000+ AI brand recommendation agents, becoming the world's largest AI influencer platform.",
                    "计划入驻 10W+ AI 品牌推荐官，成为全球最大的 AI 达人平台。",
                  ),
                ],
              },
            ].map((milestone, index) => (
              <div
                key={milestone.year}
                className={`flex gap-6 py-6 ${index !== 4 ? "border-b border-foreground/10" : ""}`}
              >
                <div className="flex-shrink-0 w-24">
                  <div className="text-lg font-semibold text-muted-foreground">
                    {milestone.year}
                  </div>
                </div>
                <div className="flex-1 space-y-3">
                  {milestone.items.map((item, i) => (
                    <p
                      key={i}
                      className="text-base leading-relaxed text-foreground"
                    >
                      {item}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL OFFICES & CONTACT */}
      <section id="contact" className="relative py-32 px-6">
        <div className="mx-auto max-w-[min(1200px,92vw)]">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-center mb-6">
            {t("Global Offices & Contact", "全球布局及联系方式")}
          </h2>
          <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-16 text-lg leading-relaxed">
            {t(
              "OranAI has offices in Beijing, Shanghai, Shenzhen, Hong Kong, and Los Angeles, supporting brand globalization and growth through a multi-location collaborative organization.",
              "橙果视界在北京、上海、深圳、香港及美国洛杉矶设有办公室，以多地协同的组织布局，支持品牌全球化增长业务。",
            )}
          </p>

          <div className=" rounded-2xl nebula-border overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-8 border-b md:border-b-0 border-foreground/10">
                <h3 className="text-xl font-bold mb-2">{t("Beijing", "北京")}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {t(
                    "Room 4268, 4F, Building AB, Dongsheng Building, No. 8 Zhongguancun East Road, Haidian District, Beijing",
                    "北京市海淀区中关村东路8号东升大厦AB座4层4268号",
                  )}
                </p>
              </div>
              <div className="p-8  border-foreground/10">
                <h3 className="text-xl font-bold mb-2">{t("Shanghai", "上海")}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {t(
                    "Room 703A, No. 1819 Wuzhong Road, Minhang District, Shanghai",
                    "上海市闵行区吴中路1819号703A室",
                  )}
                </p>
              </div>
              <div className="p-8 md:border-b-0 border-foreground/10">
                <h3 className="text-xl font-bold mb-2">{t("Shenzhen", "深圳")}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {t(
                    "Room 901, Research Building, Tsinghua Information Port, Nanshan District, Shenzhen",
                    "深圳南山区清华信息港-科研楼901",
                  )}
                </p>
              </div>
              <div className="p-8  border-foreground/10">
                <h3 className="text-xl font-bold mb-2">{t("Hong Kong", "香港")}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  RM 1102 HANG SENG CASTLE PEAK RD BUILDING, 339 CASTLE PEAK ROAD, CHEUNG SHA WAN HONG KONG
                </p>
              </div>
              <div className="p-8 md:border-b-0 border-foreground/10">
                <h3 className="text-xl font-bold mb-2">{t("Los Angeles", "洛杉矶")}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  17800 CASTLETON ST STE 665, CITY OF INDUSTRY, CA 91748
                </p>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold mb-4">{t("Contact", "联系方式")}</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm font-medium">{t("Email", "邮箱")}:</span>
                    <a
                      href="mailto:hey@photog.art"
                      className="text-foreground hover:underline text-sm"
                    >
                      hey@photog.art
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm font-medium">{t("Website", "官网")}:</span>
                    <a
                      href="https://oran.cn"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:underline text-sm"
                    >
                      oran.cn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyPage;